"use strict";Object.defineProperty(exports, "__esModule", {value: true}); function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }var _posthog = require('web/services/posthog'); var _posthog2 = _interopRequireDefault(_posthog);
var _uuid = require('shared/services/uuid');

const STORAGE_KEY = 'bt_signup_attribution'
const COOKIE_NAME = 'bt_public_attribution'
const ONBOARDING_SESSION_ID_KEY = 'onboarding_session_id'
const APP_OPENED_AFTER_SIGNUP_EMITTED_KEY = 'app_opened_after_signup_emitted'
const ALLOWED_PARAM_KEYS = [
  'bt_landing_id',
  'bt_cta',
  'bt_signup_distinct_id',
  'bt_ph_distinct_id',
  'bt_ph_session_id',
  'bt_onboarding_session_id',
  'signup_distinct_id',
  'distinct_id',
  'session_id',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
]
const ALLOWED_COOKIE_KEYS = [
  'bt_landing_id',
  'first_url',
  'first_path',
  'first_referrer',
  'first_seen_at',
  'last_url',
  'last_path',
  'last_referrer',
  'last_cta',
  'last_seen_at',
  'ph_distinct_id',
  'ph_session_id',
  ...ALLOWED_PARAM_KEYS,
]
const SETUP_ATTRIBUTION_KEYS = [
  'bt_landing_id',
  'bt_cta',
  'ph_distinct_id',
  'ph_session_id',
  'signup_distinct_id',
  'onboarding_session_id',
  'first_path',
  'last_path',
  'first_referrer',
  'last_referrer',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
]








// The acquisition params the unified `signup_email_submitted` reports.
// Both copies of that event must carry an identical custom property set, because
// PostHog's dedup is a full-row replace and either copy can be the one that
// survives — a key only one side sends would blink in and out of the funnel.
// So this list, and the three value rules below, mirror the backend's
// signupSubmitAttributionParams and sanitizeSignupAttributionValue
// (apps/backend/webserver/welcome_server.go). Duplicated deliberately: the
// browser cannot import Go, and the backend already duplicates the same rules
// between its webserver and command packages for the same reason
// (command/task_signup_recovery.go). Change one, change all three.
const SIGNUP_SUBMIT_PROPERTY_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'bt_landing_id',
  'bt_cta',
]
const MAX_SIGNUP_ATTRIBUTION_VALUE_BYTES = 512

const isBrowser = () => typeof window !== 'undefined'

// Mirrors isPhoneLikeSignupAttributionValue (welcome_server.go).
const isPhoneLikeSignupAttributionValue = (value) => {
  let digitCount = 0
  for (const character of value) {
    if (character >= '0' && character <= '9') {
      digitCount += 1
      continue
    }
    if (!'+ ()-.'.includes(character)) return false
  }
  return digitCount >= 10
}

// Go's len() on a string counts BYTES, so the 512 cap it enforces is a byte
// cap; JS .length counts UTF-16 code units. Measuring bytes here is what keeps
// a multibyte value from passing one side and failing the other.
const utf8ByteLength = (value) => {
  let bytes = 0
  for (const character of value) {
    const codePoint = character.codePointAt(0) || 0
    if (codePoint <= 0x7f) bytes += 1
    else if (codePoint <= 0x7ff) bytes += 2
    else if (codePoint <= 0xffff) bytes += 3
    else bytes += 4
  }
  return bytes
}

// Mirrors sanitizeSignupAttributionValue (welcome_server.go).
const sanitizeSignupSubmitProperty = (value) => {
  const trimmed = value.trim()
  if (!trimmed || utf8ByteLength(trimmed) > MAX_SIGNUP_ATTRIBUTION_VALUE_BYTES) {
    return ''
  }
  if (trimmed.includes('@') || isPhoneLikeSignupAttributionValue(trimmed)) return ''
  return trimmed
}

const safeJsonParse = (value) => {
  if (!value) return {}
  try {
    return JSON.parse(value)
  } catch (e) {
    return {}
  }
}

const readStorage = () => {
  if (!isBrowser()) return {}

  try {
    return safeJsonParse(window.localStorage.getItem(STORAGE_KEY))
  } catch (e) {
    return {}
  }
}

const writeStorage = (attribution) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution))
  } catch (e) {
    // Ignore storage failures so signup still works.
  }
}

const readCookie = (name) => {
  try {
    const nameEQ = `${name}=`
    const cookies = document.cookie.split(';')

    for (let i = 0; i < cookies.length; i++) {
      let c = cookies[i] || ''
      while (c.charAt(0) === ' ') c = c.substring(1, c.length)
      if (c.indexOf(nameEQ) === 0) return decodeURIComponent(c.substring(nameEQ.length, c.length))
    }

    return null
  } catch (e) {
    return null
  }
}

const pick = (source, keys) => {
  const output = {}
  keys.forEach(key => {
    const value = source[key]
    if (value) output[key] = value
  })
  return output
}

const stripQueryAndHash = (value) => value.split(/[?#]/)[0] || ''

const sanitizeSetupAttributionValue = (key, value) => {
  const trimmed = value.trim()
  if (!trimmed) return ''

  if (key.endsWith('_path') || key.endsWith('_referrer')) {
    return stripQueryAndHash(trimmed)
  }

  return trimmed
}

const paramsToObject = (params) => {
  const output = {}
  ALLOWED_PARAM_KEYS.forEach(key => {
    const value = params.get(key)
    if (value) output[key] = value
  })

  const publicDistinctId = params.get('bt_ph_distinct_id') || params.get('distinct_id')
  const publicSessionId = params.get('bt_ph_session_id') || params.get('session_id')
  const signupDistinctId = params.get('bt_signup_distinct_id') || params.get('signup_distinct_id')
  const onboardingSessionId = params.get('bt_onboarding_session_id')
  if (publicDistinctId) output.ph_distinct_id = publicDistinctId
  if (publicSessionId) output.ph_session_id = publicSessionId
  if (signupDistinctId) output.signup_distinct_id = signupDistinctId
  if (onboardingSessionId) output.onboarding_session_id = onboardingSessionId

  return output
}

const getTopLevelParams = () => {
  try {
    return new URLSearchParams(window.location.search)
  } catch (e) {
    return new URLSearchParams()
  }
}

const getHashParams = () => {
  try {
    const [, hashQuery = ''] = window.location.hash.split('?')
    return new URLSearchParams(hashQuery)
  } catch (e) {
    return new URLSearchParams()
  }
}

const decodeOnce = (value) => {
  try {
    return decodeURIComponent(value)
  } catch (e) {
    return value
  }
}

const normalizeEmailParam = (value) => {
  if (!value) return ''

  const decoded = decodeOnce(value)
  return (decoded.split('&')[0] || '').trim()
}

const readCookieAttribution = () => pick(
  safeJsonParse(readCookie(COOKIE_NAME)),
  ALLOWED_COOKIE_KEYS,
)

 const getSignupEmailPrefill = () => {
  if (!isBrowser()) return ''

  return normalizeEmailParam(
    getTopLevelParams().get('email') || getHashParams().get('email'),
  )
}; exports.getSignupEmailPrefill = getSignupEmailPrefill

 const mintSignupSubmitEvent = () => ({
  id: _uuid.uuidv4.call(void 0, ),
  timestamp: new Date(),
}); exports.mintSignupSubmitEvent = mintSignupSubmitEvent

 const getSignupForwardingPath = (submitEvent) => {
  if (!isBrowser()) return ''

  const params = new URLSearchParams()
  const attribution = exports.ingestSignupAttribution.call(void 0, )

  ALLOWED_PARAM_KEYS.forEach(key => {
    const value = attribution[key]
    if (value) params.set(key, value)
  })

  if (attribution.ph_distinct_id) params.set('bt_ph_distinct_id', attribution.ph_distinct_id)
  if (attribution.ph_session_id) params.set('bt_ph_session_id', attribution.ph_session_id)
  if (attribution.signup_distinct_id) params.set('bt_signup_distinct_id', attribution.signup_distinct_id)
  if (attribution.onboarding_session_id) params.set('bt_onboarding_session_id', attribution.onboarding_session_id)
  if (submitEvent) {
    params.set('bt_submit_event_id', submitEvent.id)
    params.set('bt_submit_ts', submitEvent.timestamp.toISOString())
  }

  return params.toString()
}; exports.getSignupForwardingPath = getSignupForwardingPath

 const getSignupSubmitEventProperties = (forwardingPath) => {
  const properties = {}
  const params = new URLSearchParams(forwardingPath)

  SIGNUP_SUBMIT_PROPERTY_KEYS.forEach(key => {
    const value = sanitizeSignupSubmitProperty(params.get(key) || '')
    if (value) properties[key] = value
  })

  return properties
}; exports.getSignupSubmitEventProperties = getSignupSubmitEventProperties

 const ingestSignupAttribution = () => {
  if (!isBrowser()) return {}

  const attribution = {
    ...readStorage(),
    ...readCookieAttribution(),
    ...paramsToObject(getHashParams()),
    ...paramsToObject(getTopLevelParams()),
  }

  if (Object.keys(attribution).length > 0) {
    writeStorage(attribution)
  }

  return attribution
}; exports.ingestSignupAttribution = ingestSignupAttribution

 const getPublicPostHogDistinctId = () => exports.getSignupAttribution.call(void 0, ).ph_distinct_id; exports.getPublicPostHogDistinctId = getPublicPostHogDistinctId

 const getSignupPostHogDistinctId = () => exports.getSignupAttribution.call(void 0, ).signup_distinct_id; exports.getSignupPostHogDistinctId = getSignupPostHogDistinctId

 const getSignupAttribution = () => readStorage(); exports.getSignupAttribution = getSignupAttribution

 const getSignupSetupAttribution = () => {
  const output = {}
  const attribution = exports.getSignupAttribution.call(void 0, )

  SETUP_ATTRIBUTION_KEYS.forEach(key => {
    const value = attribution[key]
    if (!value) return

    const safeValue = sanitizeSetupAttributionValue(key, value)
    if (safeValue) output[key] = safeValue
  })

  return output
}; exports.getSignupSetupAttribution = getSignupSetupAttribution

 const getOrCreateOnboardingSessionId = () => {
  const attribution = exports.getSignupAttribution.call(void 0, )
  const existingSessionId = attribution[ONBOARDING_SESSION_ID_KEY]
  if (existingSessionId) return existingSessionId

  const onboardingSessionId = `bt_onboarding:${_uuid.uuidv4.call(void 0, )}`
  writeStorage({
    ...attribution,
    [ONBOARDING_SESSION_ID_KEY]: onboardingSessionId,
  })

  return onboardingSessionId
}; exports.getOrCreateOnboardingSessionId = getOrCreateOnboardingSessionId

 const hasEmittedAppOpenedAfterSignup = () =>
  exports.getSignupAttribution.call(void 0, )[APP_OPENED_AFTER_SIGNUP_EMITTED_KEY] === 'true'; exports.hasEmittedAppOpenedAfterSignup = hasEmittedAppOpenedAfterSignup

 const markAppOpenedAfterSignupEmitted = () => {
  const attribution = exports.getSignupAttribution.call(void 0, )
  writeStorage({
    ...attribution,
    [APP_OPENED_AFTER_SIGNUP_EMITTED_KEY]: 'true',
  })
}; exports.markAppOpenedAfterSignupEmitted = markAppOpenedAfterSignupEmitted

 const identifySignupEmail = (email) => {
  const normalizedEmail = normalizeEmailParam(email).toLowerCase()
  if (!normalizedEmail) return

  const attribution = exports.ingestSignupAttribution.call(void 0, )
  // The fallback id must be PER-SIGNUP: the old `bt_signup:{bt_landing_id}`
  // form was landing-scoped, i.e. shared by every signup this browser ever
  // makes — identifying it re-attached each new signup to the first user's
  // person (verified live: an entire later signup's event stream captured
  // under the first signup's bt_signup id). A fresh UUID is minted per
  // signup and persisted in the attribution store, which is dropped when the
  // next signup severs the previous identity.
  const signupDistinctId = attribution.signup_distinct_id
    || `bt_signup:${_uuid.uuidv4.call(void 0, )}`

  if (!signupDistinctId) return

  const nextAttribution = {
    ...attribution,
    signup_distinct_id: signupDistinctId,
  }
  writeStorage(nextAttribution)

  _posthog2.default.identify(signupDistinctId, {
    email: normalizedEmail,
    bt_landing_id: nextAttribution.bt_landing_id,
    signup_source: 'webchat_signup',
  })
}; exports.identifySignupEmail = identifySignupEmail

/**
 * Severs a previous session's analytics identity before a NEW signup starts.
 * Without this, a signup on a machine where someone else was logged in (or
 * where the auto-bridge skipped the logout action) captures its events under
 * the previous user's identified lineage, and the later identify/alias chain
 * merges the new user into that person — observed live as every same-browser
 * signup resolving to one PostHog person. An anonymous (possibly
 * website-bootstrapped) lineage is deliberately kept: merging IT into the new
 * user at identify time is the desired attribution.
 *
 * The STORED signup attribution is dropped too: its `signup_distinct_id` (and
 * website ids) persist per-browser, not per-signup, and the post-signup alias
 * of that id to each new user key re-chained every signup into one person
 * even after the identity reset. The new signup's own URL params repopulate
 * attribution immediately afterwards via ingestSignupAttribution.
 */
 const severStaleAnalyticsIdentity = () => {
  if (!_posthog2.default.isIdentified()) return
  exports.clearSignupAttribution.call(void 0, )
  _posthog2.default.reset()
}; exports.severStaleAnalyticsIdentity = severStaleAnalyticsIdentity

/**
 * Drops the stored signup attribution. Called on logout: the stored
 * `signup_distinct_id` is per-signup, and leaving it behind lets the login
 * middleware alias it to the NEXT user who logs in on this browser (front-desk
 * shared machines), merging two people into one PostHog person. Signup entry
 * points sever it too, but the direct-login path never passes through them.
 */
 const clearSignupAttribution = () => {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch (e) {
    // best-effort; storage may be unavailable
  }
}; exports.clearSignupAttribution = clearSignupAttribution

 const captureSignupEvent = (
  event,
  properties = {},
) => {
  _posthog2.default.capture(event, {
    ...exports.getSignupAttribution.call(void 0, ),
    ...properties,
  })
}; exports.captureSignupEvent = captureSignupEvent
